<template>
    <div>
        <page-header>
            API Tokens

            <template v-slot:actions>
                <vx-button @click.prevent="createToken()" type="button" size="lg">
                    Create API Token <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </template>
        </page-header>

        <page-loading v-if="loading && !tokens.length"></page-loading>

        <vx-box
            color="info"
            v-else-if="!loading && !tokens.length"
            class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
        >
            You have not created any API Tokens.
            <vx-button data-test="add-button" @click.prevent="createToken()" type="button">
                Create API Token <font-awesome-icon widthAuto :icon="faCirclePlus" />
            </vx-button>
        </vx-box>

        <vx-table v-else-if="tokens.length" class="xl:grid-cols-4-auto" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div class="xl:col-span-2">Name</div>
                <div>Created</div>
            </vx-table-row>

            <vx-table-row v-for="(token, index) in tokens" :key="index">
                <vx-table-row-title class="xl:col-span-2!">
                    {{ token.name }}
                </vx-table-row-title>

                <vx-table-row-item>
                    <template v-slot:label> Created: </template>
                    {{ dateTime(token.created_at) }}
                </vx-table-row-item>

                <vx-table-row-buttons>
                    <vx-dropdown placement="right">
                        <vx-button
                            data-test="dropdown"
                            aria-label="Token actions"
                            type="button"
                            color="muted"
                            outline
                            class="w-[45px]"
                        >
                            <font-awesome-icon widthAuto :icon="faEllipsis" />
                        </vx-button>

                        <template v-slot:items>
                            <vx-dropdown-item data-test="delete" @click="deleteToken(token)">
                                Delete
                                <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                            </vx-dropdown-item>
                        </template>
                    </vx-dropdown>

                    <vx-button
                        @click.prevent="editToken(token)"
                        data-test="edit-button"
                        color="muted"
                        class="w-full sm:w-auto"
                        outline
                    >
                        Edit <font-awesome-icon widthAuto :icon="faFilePen" />
                    </vx-button>
                </vx-table-row-buttons>
            </vx-table-row>
        </vx-table>

        <api-token-form-modal v-model="createOrUpdateToken"></api-token-form-modal>
    </div>
</template>

<script>
import { getClientTz } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import axios from '~/services/axios';
import {
    VxBox,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxSwal,
    VxTable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import ApiTokenFormModal from '~/components/settings/api/ApiTokenFormModal.vue';
import { throwSwalRequestError } from '~/utils/request';
import { escapeHtml } from '~/utils/html';
import PageHeader from '~/components/general/PageHeader.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faEllipsis, faFilePen, faCirclePlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import PageLoading from '~/components/general/PageLoading.vue';

export default {
    props: ['tokens', 'loading'],
    components: {
        ApiTokenFormModal,
        PageHeader,
        VxTable,
        VxTableRow,
        VxTableRowTitle,
        VxTableRowButtons,
        VxTableRowItem,
        VxDropdown,
        VxDropdownItem,
        VxButton,
        FontAwesomeIcon,
        PageLoading,
        VxBox,
    },

    /**
     * The component's data.
     */
    data() {
        return {
            createOrUpdateToken: null,
        };
    },

    computed: {
        faCirclePlus: () => faCirclePlus,
        faEllipsis: () => faEllipsis,
        faTrash: () => faTrash,
        faFilePen: () => faFilePen,
    },

    methods: {
        dateTime(value) {
            return dayjs.utc(value).tz(getClientTz()).format('MMMM Do, YYYY h:mm A');
        },

        createToken() {
            this.createOrUpdateToken = { name: '' };
        },
        editToken(token) {
            this.createOrUpdateToken = { ...token };
        },

        deleteToken(token) {
            VxSwal.fire({
                title: 'Delete Token',
                icon: 'danger',
                showCancelButton: true,
                confirmButtonColor: 'danger',
                confirmButtonText: 'Yes, Delete',
                html: `Are you sure you want to delete this token <strong class="font-bold">"${escapeHtml(
                    token.name
                )}"</strong>? If deleted, API requests that attempt to authenticate using this token will no longer be accepted.`,
                preConfirm: () =>
                    axios
                        .delete(`/settings/api/token/${token.id}`)
                        .then(() => {
                            Bus.$emit('updateTokens');
                        })
                        .catch(throwSwalRequestError),
            });
        },
    },
};
</script>
